import { isDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { cashApi } from '@/store/cash/api/index.ts';
import { getRequestCashRfsMutationCacheKey } from '@/store/cash/selectors/rfs.ts';
import {
  cashErrorOccurred,
  cashRequestCanceled,
  cashRfsQuoteReceived,
  cashRfsQuoteRequested,
} from '@/store/cash/slice.ts';
import type { AppDispatch } from '@/store/store.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { IQuoteAbort, QuoteWithPrice, ReceivedCashQuoteReply } from '@/utils/rfs/type.ts';
import { toPredealCheck } from '@/utils/rfs/utils.ts';

export type RequestCashRfsParams = RequestForwardRfsParams | RequestNdfRfsParams;

export type RequestForwardRfsParams = {
  tileId: string;
  productName: 'FxFwd' | 'FxSpot';
  signalRConnectionId: string;
  clientId: number;

  currency1: string;
  currency2: string;
  isCurrencyPairInverted: boolean;

  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  maturityDate: string; // ISO date like "2025-04-04T00:00:00.000Z"
  currencyPairPrecision: number;
};

export type RequestNdfRfsParams = {
  tileId: string;
  productName: 'FxNdf';
  signalRConnectionId: string;
  clientId: number;

  currency1: string;
  currency2: string;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  maturityDate: string; // ISO date like "2025-04-04T00:00:00.000Z"
  currencyPairPrecision: number;

  // non-deliverable and deliverable currency pair
  fixingDate: string;
  fixingCurrency: string;
  fixingSource: string;

  // non-deliverable currency pair only
  sndFixingSource?: string;
  xCurrency?: string;
};

export type CashRfsBody = {
  fxCashProduct: CashRfsForwardProduct | CashRfsNdfProduct;

  replyToStream: string;
  rfsId: string; // use generateRfsId() to create a new rfs ID
  requesterCounterpartyId: number;
  forceTradable: boolean;
};

export type CashRfsForwardProduct = {
  cashType: 'FxSpot' | 'FxFwd';
  currencyPair: string;
  currency1: string;
  currency2: string;
  negociatedAmount: number;
  negociatedCurrency: 1 | 2;
  expiryDate: string; // ISO date
  spotPrecision: number;
};

export type CashRfsNdfProduct = {
  cashType: 'FxNdf';
  currencyPair: string;
  currency1: string;
  currency2: string;
  negociatedAmount: number;
  negociatedCurrency: 1 | 2;
  expiryDate: string; // ISO date
  spotPrecision: number;

  // non-deliverable and deliverable currency pair
  fixingDate: string;
  fixingCurrency: string;
  fixingSource: string;

  // non-deliverable currency pair only
  sndFixingSource?: string;
  xCurrency?: string;
};

export type RequestCashRfsResponse = {};

export const requestCashRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<RequestCashRfsResponse, RequestCashRfsParams & { rfsId: string }>({
    query: (params) => {
      const body = params.productName === 'FxNdf' ? getNdfRfsBody(params) : getForwardRfsBody(params);

      return {
        url: '/fxSpotFwd/rfs/request',
        method: 'POST',
        body,
        headers: {
          'X-Correlation-Id': params.rfsId,
        },
      };
    },

    async onCacheEntryAdded(params, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
      dispatch(cashRfsQuoteRequested({ tileId: params.tileId, rfsId: params.rfsId, requestedAt: Date.now() }));

      // wait for the initial query to resolve before proceeding
      await cacheDataLoaded;

      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(params.tileId, params.rfsId, dispatch);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(params.tileId, params.rfsId, dispatch);

      const signalRConnector = getSignalRConnector();

      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      // to close the RFS stream, call:
      // closeCashRfs(tileId, dispatch);
      // `cacheEntryRemoved` is always resolved explicitly by `closeCashRfs` (via `removeMutationResult`),
      // never by RTK Query's automatic cache expiry. All stream closure paths (QuoteAbort, stream timeout,
      // heartbeat timeout, user cancel) call `cancelCashRfs` → `closeCashRfs`.
      await cacheEntryRemoved;

      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      dispatch(cashRequestCanceled({ tileId: params.tileId }));
    },
  });
};

// To close the listeners, call closeCashRfs(tileId, dispatch)
// for request called with:
// const [requestCashRfs] = useRequestCashRfsMutation({ fixedCacheKey: getRequestCashRfsMutationCacheKey(tileId) });
export const closeCashRfs = (tileId: string, dispatch: AppDispatch) => {
  dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: getRequestCashRfsMutationCacheKey(tileId) }));
  dispatch(cashRequestCanceled({ tileId }));
};

export const getForwardRfsBody = (request: RequestForwardRfsParams & { rfsId: string }) => {
  return {
    fxCashProduct: {
      currencyPair: `${request.currency1}/${request.currency2}`,
      currency1: request.currency1,
      currency2: request.currency2,
      negociatedAmount: request.notional,
      // negociatedCurrency: request.notionalCurrencyIndex,
      negociatedCurrency: request.notionalCurrencyIndex,
      expiryDate: request.maturityDate,
      spotPrecision: request.currencyPairPrecision,
      cashType: request.productName,
    },
    replyToStream: request.signalRConnectionId,
    rfsId: request.rfsId,
    requesterCounterpartyId: request.clientId,
    // Cash has no indicative-quote flow (unlike Option/Accumulator tiles), so quotes are always tradable
    forceTradable: true,
  } satisfies CashRfsBody;
};

export const getNdfRfsBody = (request: RequestNdfRfsParams & { rfsId: string }) => {
  return {
    fxCashProduct: {
      cashType: 'FxNdf',
      currencyPair: `${request.currency1}/${request.currency2}`,
      currency1: request.currency1,
      currency2: request.currency2,

      expiryDate: request.maturityDate,
      spotPrecision: request.currencyPairPrecision,

      negociatedAmount: request.notional,
      negociatedCurrency: request.notionalCurrencyIndex,

      fixingDate: request.fixingDate,
      fixingCurrency: request.fixingCurrency,
      fixingSource: request.fixingSource,

      // cross currency
      sndFixingSource: request.sndFixingSource,
      xCurrency: request.xCurrency,
    },
    replyToStream: request.signalRConnectionId,
    rfsId: request.rfsId,
    requesterCounterpartyId: request.clientId,
    // Cash has no indicative-quote flow (unlike Option/Accumulator tiles), so quotes are always tradable
    forceTradable: true,
  } satisfies CashRfsBody;
};

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

function buildHandleReceiveQuoteReply(tileId: string, rfsId: string, dispatch: AppDispatch) {
  return (reply: QuoteWithPrice<ReceivedCashQuoteReply>) => {
    if (reply.CashQuoteReply?.RfsId !== rfsId) {
      return;
    }

    dispatch(
      cashRfsQuoteReceived({
        tileId,
        backTimestamp: reply.Timestamp,
        frontTimestamp: reply.workerTimestamp,
        rfsId,
        quoteType: reply.Type,
        reply: reply.CashQuoteReply,
      }),
    );
  };
}

const buildHandleReceiveQuoteAbort =
  (tileId: string, rfsId: string, dispatch: AppDispatch) => (reject: IQuoteAbort) => {
    if (reject.RequestId !== rfsId) {
      return;
    }

    closeCashRfs(tileId, dispatch);

    if (isDefined(reject.QuoteReject)) {
      // is it the end of the RFS window ?
      if (reject.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
        // it is not an error. the RFS is just over
        return;
      }

      console.warn('RFS Quote aborted:', reject.QuoteReject.ErrorMessage);

      dispatch(
        cashErrorOccurred({
          tileId,
          errorType: 'rate',
          errorCode: reject.QuoteReject.ErrorCode,
          errorMessage: reject.QuoteReject.ErrorMessage,

          multipass: {
            errorCode: reject.QuoteReject.MultipassErrorCode,
            sessionId: reject.QuoteReject.MultipassSessionId,
            predealChecks: reject.QuoteReject.PredealChecks.map(toPredealCheck),
          },
        }),
      );
    }
  };
